import React from 'react';
import { Trash2Icon, XIcon, EyeIcon } from 'lucide-react';
import type { Question, VisibilityCondition } from './types';

const ELIGIBLE_TRIGGER_TYPES = ['multipleChoice', 'checkbox', 'rating'];

const QUESTION_TYPE_LABELS: Record<string, string> = {
  text: 'Text',
  multipleChoice: 'Multiple Choice',
  checkbox: 'Checkbox',
  rating: 'Rating',
};

const getValuesForQuestion = (question: Question): string[] => {
  if (question.type === 'rating') {
    const max = question.ratingScale || 5;
    return Array.from({ length: max }, (_, i) => String(i + 1));
  }
  return question.options?.filter(Boolean) || [];
};

interface QuestionCardProps {
  question: Question;
  globalIndex: number;
  allQuestions: Question[];
  isReadOnly: boolean;
  dragHandle?: React.ReactNode;
  onRemove: (questionId: string) => void;
  onQuestionChange: (questionId: string, field: keyof Question, value: any) => void;
  onTypeChange: (questionId: string, type: string) => void;
  onOptionChange: (questionId: string, index: number, value: string) => void;
  onAddOption: (questionId: string) => void;
  onRemoveOption: (questionId: string, index: number) => void;
  onToggleCondition: (globalIndex: number) => void;
  onConditionChange: (globalIndex: number, field: keyof VisibilityCondition, value: any) => void;
  onConditionValueToggle: (globalIndex: number, value: string) => void;
  onToggleFlag: (questionId: string) => void;
  onFlagChange: (questionId: string, field: string, value: any) => void;
  onFlagValueToggle: (questionId: string, value: string) => void;
}

export const QuestionCard: React.FC<QuestionCardProps> = ({
  question,
  globalIndex,
  allQuestions,
  isReadOnly,
  dragHandle,
  onRemove,
  onQuestionChange,
  onTypeChange,
  onOptionChange,
  onAddOption,
  onRemoveOption,
  onToggleCondition,
  onConditionChange,
  onConditionValueToggle,
  onToggleFlag,
  onFlagChange,
  onFlagValueToggle,
}) => {
  const precedingEligible = allQuestions
    .map((q, i) => ({ q, i }))
    .filter(({ q, i }) => i < globalIndex && ELIGIBLE_TRIGGER_TYPES.includes(q.type));
  const canHaveCondition = precedingEligible.length > 0;
  const triggerQuestion = question.visibilityCondition
    ? allQuestions[question.visibilityCondition.triggerQuestionIndex]
    : null;
  const triggerValues = triggerQuestion ? getValuesForQuestion(triggerQuestion) : [];

  return (
    <div className="group bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 border-l-4 border-l-blue-400 dark:border-l-blue-500 hover:shadow-md transition-shadow overflow-hidden">
      <div className="bg-gray-50 dark:bg-gray-700/50 px-6 py-3 flex items-center justify-between">
        <div className="flex items-center gap-2">
          {dragHandle}
          <span className="bg-blue-100 dark:bg-blue-900/50 text-blue-700 dark:text-blue-300 rounded-full px-2.5 py-0.5 text-xs font-medium">
            {globalIndex + 1}
          </span>
          <span className="bg-gray-100 dark:bg-gray-600 text-gray-600 dark:text-gray-300 rounded-full px-2.5 py-0.5 text-xs font-medium">
            {QUESTION_TYPE_LABELS[question.type] || question.type}
          </span>
          {question.required && (
            <span className="bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300 rounded-full px-2.5 py-0.5 text-xs font-medium">
              Required
            </span>
          )}
          {question.flagConfig && (
            <span className="bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300 rounded-full px-2.5 py-0.5 text-xs font-medium">
              🚩 Flag
            </span>
          )}
        </div>
        {!isReadOnly && (
          <button
            onClick={() => onRemove(question.id)}
            className="p-1 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-md transition-all opacity-0 group-hover:opacity-100"
          >
            <Trash2Icon className="h-4 w-4 text-red-500" />
          </button>
        )}
      </div>

      <div className="p-6 space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Question Type
            </label>
            <select
              value={question.type}
              onChange={(e) => onTypeChange(question.id, e.target.value)}
              disabled={isReadOnly}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white disabled:opacity-60 disabled:cursor-not-allowed disabled:bg-gray-50 dark:disabled:bg-gray-800/50"
            >
              <option value="text">Text</option>
              <option value="multipleChoice">Multiple Choice</option>
              <option value="checkbox">Checkbox</option>
              <option value="rating">Rating</option>
            </select>
          </div>

          <div className="flex items-center">
            <input
              type="checkbox"
              id={`required-${question.id}`}
              checked={question.required}
              onChange={(e) => onQuestionChange(question.id, 'required', e.target.checked)}
              disabled={isReadOnly}
              className="mr-2 disabled:opacity-60 disabled:cursor-not-allowed"
            />
            <label
              htmlFor={`required-${question.id}`}
              className="text-sm text-gray-700 dark:text-gray-300"
            >
              Required
            </label>
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Question Text
          </label>
          <input
            type="text"
            value={question.text}
            onChange={(e) => onQuestionChange(question.id, 'text', e.target.value)}
            disabled={isReadOnly}
            className="w-full px-3 py-2 text-base border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white disabled:opacity-60 disabled:cursor-not-allowed disabled:bg-gray-50 dark:disabled:bg-gray-800/50"
            placeholder="Enter question text"
          />
        </div>

        {(question.type === 'multipleChoice' || question.type === 'checkbox') && (
          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
              Options
            </label>
            {question.options?.map((option, optionIndex) => (
              <div key={optionIndex} className="flex items-center gap-2 ml-2">
                <span className="flex-shrink-0 w-6 h-6 rounded-full bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400 flex items-center justify-center text-xs font-medium">
                  {optionIndex + 1}
                </span>
                <input
                  type="text"
                  value={option}
                  onChange={(e) => onOptionChange(question.id, optionIndex, e.target.value)}
                  disabled={isReadOnly}
                  className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white disabled:opacity-60 disabled:cursor-not-allowed disabled:bg-gray-50 dark:disabled:bg-gray-800/50"
                  placeholder={`Option ${optionIndex + 1}`}
                />
                {!isReadOnly && (
                  <button
                    onClick={() => onRemoveOption(question.id, optionIndex)}
                    className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-md transition-colors"
                  >
                    <XIcon className="h-4 w-4 text-red-500" />
                  </button>
                )}
              </div>
            ))}
            {!isReadOnly && (
              <button
                onClick={() => onAddOption(question.id)}
                className="ml-2 text-sm text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300"
              >
                + Add Option
              </button>
            )}
          </div>
        )}

        {question.type === 'rating' && (
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Rating Scale
            </label>
            <select
              value={question.ratingScale || 5}
              onChange={(e) =>
                onQuestionChange(question.id, 'ratingScale', Number(e.target.value))
              }
              disabled={isReadOnly}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white disabled:opacity-60 disabled:cursor-not-allowed disabled:bg-gray-50 dark:disabled:bg-gray-800/50"
            >
              <option value={5}>1-5</option>
              <option value={10}>1-10</option>
            </select>
          </div>
        )}

        {!isReadOnly && canHaveCondition && (
          <div className="border-t border-gray-200 dark:border-gray-700 pt-4 mt-4">
            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                id={`condition-${question.id}`}
                checked={!!question.visibilityCondition}
                onChange={() => onToggleCondition(globalIndex)}
                className="mr-1"
              />
              <EyeIcon className="h-4 w-4 text-gray-500 dark:text-gray-400" />
              <label
                htmlFor={`condition-${question.id}`}
                className="text-sm text-gray-700 dark:text-gray-300"
              >
                Show only when condition is met
              </label>
            </div>

            {question.visibilityCondition && (
              <div className="mt-3 ml-6 space-y-3 p-3 bg-amber-50/50 dark:bg-amber-900/10 rounded-md border border-amber-200 dark:border-amber-800/50">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                      When answer to
                    </label>
                    <select
                      value={question.visibilityCondition.triggerQuestionIndex}
                      onChange={(e) =>
                        onConditionChange(globalIndex, 'triggerQuestionIndex', Number(e.target.value))
                      }
                      className="w-full px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                    >
                      {precedingEligible.map(({ q: pq, i: pi }) => (
                        <option key={pi} value={pi}>
                          Q{pi + 1}: {pq.text || '(untitled)'}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                      Operator
                    </label>
                    <select
                      value={question.visibilityCondition.operator}
                      onChange={(e) =>
                        onConditionChange(globalIndex, 'operator', e.target.value)
                      }
                      className="w-full px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                    >
                      <option value="anyOf">is any of</option>
                      <option value="noneOf">is none of</option>
                    </select>
                  </div>
                </div>

                {triggerValues.length > 0 && (
                  <div>
                    <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                      Values
                    </label>
                    <div className="flex flex-wrap gap-2">
                      {triggerValues.map((val) => (
                        <label
                          key={val}
                          className="inline-flex items-center space-x-1.5 text-sm cursor-pointer"
                        >
                          <input
                            type="checkbox"
                            checked={question.visibilityCondition!.values.includes(val)}
                            onChange={() => onConditionValueToggle(globalIndex, val)}
                            className="text-blue-600 focus:ring-blue-500"
                          />
                          <span className="text-gray-700 dark:text-gray-300">{val}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {!isReadOnly && ELIGIBLE_TRIGGER_TYPES.includes(question.type) && (
          <div className="border-t border-gray-200 dark:border-gray-700 pt-4 mt-4">
            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                id={`flag-${question.id}`}
                checked={!!question.flagConfig}
                onChange={() => onToggleFlag(question.id)}
                className="mr-1"
              />
              <label
                htmlFor={`flag-${question.id}`}
                className="text-sm text-gray-700 dark:text-gray-300"
              >
                🚩 Flag this question
              </label>
            </div>

            {question.flagConfig && (
              <div className="mt-3 ml-6 space-y-3 bg-red-50 dark:bg-red-900/20 rounded-lg p-3 border border-red-200 dark:border-red-800/50">
                <div>
                  <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                    Flag Label
                  </label>
                  <input
                    type="text"
                    value={question.flagConfig.label}
                    onChange={(e) => onFlagChange(question.id, 'label', e.target.value)}
                    className="w-full px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 dark:bg-gray-700 dark:text-white"
                    placeholder="e.g. At Risk"
                  />
                </div>

                {question.type === 'rating' ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                        Operator
                      </label>
                      <select
                        value={question.flagConfig.condition.operator}
                        onChange={(e) => onFlagChange(question.id, 'condition.operator', e.target.value)}
                        className="w-full px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 dark:bg-gray-700 dark:text-white"
                      >
                        <option value="lessThanOrEqual">is less than or equal to</option>
                        <option value="greaterThanOrEqual">is greater than or equal to</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                        Threshold
                      </label>
                      <input
                        type="number"
                        min={1}
                        max={question.ratingScale || 5}
                        value={question.flagConfig.condition.values[0] || ''}
                        onChange={(e) => onFlagChange(question.id, 'condition.values', [e.target.value])}
                        className="w-full px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 dark:bg-gray-700 dark:text-white"
                      />
                    </div>
                  </div>
                ) : (
                  <>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                        Operator
                      </label>
                      <select
                        value={question.flagConfig.condition.operator}
                        onChange={(e) => onFlagChange(question.id, 'condition.operator', e.target.value)}
                        className="w-full px-2 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 dark:bg-gray-700 dark:text-white"
                      >
                        <option value="anyOf">is any of</option>
                        <option value="noneOf">is none of</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1">
                        Values
                      </label>
                      <div className="flex flex-wrap gap-2">
                        {(question.options || getValuesForQuestion(question)).map((val) => (
                          <label
                            key={val}
                            className="inline-flex items-center space-x-1.5 text-sm cursor-pointer"
                          >
                            <input
                              type="checkbox"
                              checked={question.flagConfig!.condition.values.includes(val)}
                              onChange={() => onFlagValueToggle(question.id, val)}
                              className="text-red-600 focus:ring-red-500"
                            />
                            <span className="text-gray-700 dark:text-gray-300">{val}</span>
                          </label>
                        ))}
                      </div>
                    </div>
                  </>
                )}
              </div>
            )}
          </div>
        )}

        {isReadOnly && question.visibilityCondition && triggerQuestion && (
          <div className="border-t border-gray-200 dark:border-gray-700 pt-3 mt-3">
            <div className="flex items-center space-x-2 text-sm text-gray-500 dark:text-gray-400">
              <EyeIcon className="h-4 w-4" />
              <span>
                Visible when Q{question.visibilityCondition.triggerQuestionIndex + 1} ({triggerQuestion.text || 'untitled'})
                {' '}{question.visibilityCondition.operator === 'anyOf' ? 'is any of' : 'is none of'}{' '}
                {question.visibilityCondition.values.join(', ')}
              </span>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
